import React, { useState, useEffect } from "react";
import { Plus, X, Package, Check, RefreshCw, AlertCircle, Tag, Folder, Layers } from "lucide-react";
import { pb } from "../../services/pocketbase";
import { produtoDadosService } from "../../services/produtoDadosService";
import { getBaseSku, cleanSku } from "../../utils/skuUtils";

/**
 * CriarNovoProdutoModal.js — Modal de Cadastro de Novo Produto / SKU Pai Canônico
 *
 * Funcionalidades:
 * - Criação de novo SKU Pai com sanitização automática por getBaseSku
 * - Seleção de Categoria iSet e Tipo de Joia
 * - Definição de Preço de Venda Inicial e Estado de Modelagem
 * - Gravação direta em `produto_dados` e sincronização com `catalog`
 */
export default function CriarNovoProdutoModal({ isOpen, onClose, onSuccess }) {
  const [skuInput, setSkuInput] = useState("");
  const [nomeInput, setNomeInput] = useState("");
  const [categoriaId, setCategoriaId] = useState("");
  const [tipoId, setTipoId] = useState("");
  const [precoInput, setPrecoInput] = useState("");
  const [estadoModelagem, setEstadoModelagem] = useState("sem_modelo");
  const [observacoesInput, setObservacoesInput] = useState("");

  const [categoriasList, setCategoriasList] = useState([]);
  const [tiposList, setTiposList] = useState([]);
  const [loadingTaxonomia, setLoadingTaxonomia] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Carrega categorias e tipos de joia do PocketBase
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    const fetchTaxonomia = async () => {
      setLoadingTaxonomia(true);
      setErrorMsg("");
      try {
        const [cats, tipos] = await Promise.all([
          pb.collection("produto_categorias").getFullList({ sort: "nome" }),
          pb.collection("produto_tipos").getFullList({ sort: "nome" })
        ]);
        if (active) {
          setCategoriasList(cats || []);
          setTiposList(tipos || []);
        }
      } catch (err) {
        console.warn("Erro ao carregar taxonomia no modal de novo produto:", err);
      } finally {
        if (active) setLoadingTaxonomia(false);
      }
    };
    fetchTaxonomia();
    return () => { active = false; };
  }, [isOpen]);

  if (!isOpen) return null;

  // Calculado dinamicamente: SKU Pai Sanitizado
  const cleanRaw = cleanSku(skuInput);
  const calculatedSkuPai = getBaseSku(cleanRaw);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!skuInput.trim()) return setErrorMsg("Informe o SKU do produto.");
    if (!nomeInput.trim()) return setErrorMsg("Informe o Nome do produto.");

    setSaving(true);
    setErrorMsg("");
    try {
      const finalSkuPai = calculatedSkuPai || cleanRaw.toUpperCase();
      const priceVal = Number(precoInput.replace(",", "."));
      const priceCentavos = !isNaN(priceVal) && priceVal > 0 ? Math.round(priceVal * 100) : null;

      const payloadDados = {
        sku_pai: finalSkuPai,
        categoria: categoriaId || null,
        tipo_joia: tipoId || null,
        estado_modelagem: estadoModelagem,
        observacoes_internas: observacoesInput.trim()
      };

      if (!isNaN(priceVal) && priceVal > 0) {
        payloadDados.preco_venda_manual = priceVal;
        payloadDados.preco_venda_centavos = priceCentavos;
      }

      await produtoDadosService.upsertProdutoDados(finalSkuPai, payloadDados);

      // 2. Garante registro no catálogo (se não existir)
      try {
        const existingCatalog = await pb.collection("catalog").getFullList({
          filter: `sku = "${finalSkuPai}"`
        });

        const selectedCatObj = categoriasList.find(c => c.id === categoriaId);
        const catalogPayload = {
          sku: finalSkuPai,
          name: nomeInput.trim(),
          price: !isNaN(priceVal) && priceVal > 0 ? priceVal : 0,
          category: selectedCatObj ? selectedCatObj.nome : "",
          categories_id: selectedCatObj?.categories_id || null,
          full_data: {
            sku: finalSkuPai,
            name: nomeInput.trim(),
            price: !isNaN(priceVal) && priceVal > 0 ? priceVal : 0,
            category: selectedCatObj ? selectedCatObj.nome : "",
            status: "active",
            created_manually: true
          }
        };

        if (existingCatalog && existingCatalog.length > 0) {
          await pb.collection("catalog").update(existingCatalog[0].id, catalogPayload);
        } else {
          await pb.collection("catalog").create(catalogPayload);
        }
      } catch (catErr) {
        console.warn("Aviso ao sincronizar catálogo para novo produto:", catErr);
      }

      // Limpa formulário e fecha
      setSkuInput("");
      setNomeInput("");
      setCategoriaId("");
      setTipoId("");
      setPrecoInput("");
      setObservacoesInput("");

      if (onSuccess) onSuccess(finalSkuPai);
      onClose();
    } catch (err) {
      console.error("Erro ao cadastrar novo produto:", err);
      setErrorMsg("Erro ao criar produto: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* HEADER DO MODAL */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-600 rounded-xl text-white">
              <Package size={20} />
            </div>
            <div>
              <h2 className="text-base font-bold tracking-tight">Criar Novo Produto (SKU Pai)</h2>
              <p className="text-xs text-slate-400">Cadastre uma nova peça na estrutura canônica do sistema.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* FORMULÁRIO */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs overflow-y-auto">
          
          {errorMsg && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
              <AlertCircle size={16} className="shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* SKU PAI & ANCORAGEM */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                Código / SKU do Produto *
              </label>
              <input
                type="text"
                value={skuInput}
                onChange={(e) => setSkuInput(e.target.value)}
                placeholder="Ex: DIR-AUR, MMADA-BER..."
                className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2.5 font-mono font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none uppercase"
                required
              />
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                SKU Pai Canônico Resolvido
              </label>
              <div className="w-full bg-slate-100 border border-slate-200 rounded-lg p-2.5 font-mono font-bold text-blue-700 text-xs flex items-center gap-1">
                <Tag size={13} className="text-blue-500 shrink-0" />
                <span>{calculatedSkuPai || "Digite o SKU..."}</span>
              </div>
            </div>
          </div>

          {/* NOME DO PRODUTO */}
          <div>
            <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
              Nome Completo do Produto *
            </label>
            <input
              type="text"
              value={nomeInput}
              onChange={(e) => setNomeInput(e.target.value)}
              placeholder="Ex: Anel Formatura Direito em Ouro 18K com Zircônias"
              className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2.5 font-semibold text-slate-900 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
          </div>

          {/* HIERARQUIA: CATEGORIA & TIPO */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                Categoria iSet (E-commerce)
              </label>
              <select
                value={categoriaId}
                onChange={(e) => setCategoriaId(e.target.value)}
                disabled={loadingTaxonomia}
                className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2.5 font-semibold text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
              >
                <option value="">Selecione a Categoria...</option>
                {categoriasList.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nome} {cat.categories_id ? `(ID: ${cat.categories_id})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                Tipo de Joia / Prefixo
              </label>
              <select
                value={tipoId}
                onChange={(e) => setTipoId(e.target.value)}
                disabled={loadingTaxonomia}
                className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2.5 font-semibold text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
              >
                <option value="">Selecione o Tipo...</option>
                {tiposList.map(tipo => (
                  <option key={tipo.id} value={tipo.id}>
                    {tipo.nome} ({tipo.prefixo_sku || "SEM PREFIXO"})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* PREÇO DE VENDA & ESTADO DE MODELAGEM */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                Preço de Venda Inicial (R$)
              </label>
              <input
                type="text"
                value={precoInput}
                onChange={(e) => setPrecoInput(e.target.value)}
                placeholder="Ex: 219.90"
                className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2.5 font-mono font-bold text-emerald-700 focus:bg-white focus:ring-2 focus:ring-emerald-500 outline-none"
              />
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                Estado de Modelagem 3D
              </label>
              <select
                value={estadoModelagem}
                onChange={(e) => setEstadoModelagem(e.target.value)}
                className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2.5 font-semibold text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
              >
                <option value="sem_modelo">Sem Modelo (sem_modelo)</option>
                <option value="em_modelagem">Em Modelagem (em_modelagem)</option>
                <option value="render_pendente">Render Pendente (render_pendente)</option>
                <option value="pronto">Modelo Pronto (pronto)</option>
                <option value="publicado">Modelo Publicado (publicado)</option>
              </select>
            </div>
          </div>

          {/* OBSERVAÇÕES INTERNAS */}
          <div>
            <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
              Observações Internas / Especificações Inicial
            </label>
            <textarea
              rows="2"
              value={observacoesInput}
              onChange={(e) => setObservacoesInput(e.target.value)}
              placeholder="Digite observações sobre o projeto ou peça..."
              className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-medium text-slate-800 outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* BOTÕES DE AÇÃO */}
          <div className="pt-4 flex items-center justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 font-semibold hover:bg-slate-100 rounded-xl transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all shadow-md flex items-center gap-1.5 active:scale-95"
            >
              {saving ? (
                <>
                  <RefreshCw size={15} className="animate-spin" />
                  <span>Cadastrando...</span>
                </>
              ) : (
                <>
                  <Plus size={15} />
                  <span>Criar & Abrir Ficha</span>
                </>
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
